import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useItem, useItems, type ItemListEntry } from '@/hooks/use-items';
import { useT } from '@/i18n/use-t';

/** How many matches the picker lists at once — enough to choose from, small enough to scan. */
export const ITEM_PICKER_PAGE_SIZE = 20;

/** Wait this long after the last keystroke before searching. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * RL-586 — choose the catalog item an `item` hotkey adds, by searching this
 * tenant's catalogue (name or SKU contains, barcode exact — the same
 * `GET /catalog/items?search=` the items page uses) instead of pasting its
 * database id.
 *
 * Ids differ between tenants: an id copied from yuki-staging into production
 * yuki is a different (or no) item there, which is how the bare 422 this
 * replaces was hit. Searching the tenant the operator is signed into can only
 * offer that tenant's own items.
 *
 * Only active items are offered — an archived item can't be sold, so a
 * hotkey to it would be a dead button. The chosen item's id still shows under
 * its name, for support.
 */
export function ItemSearchPicker({
  value,
  onChange,
}: {
  /** The selected item's id, or '' when none is chosen. */
  value: string;
  onChange: (item: { id: string; name: string } | null) => void;
}) {
  const t = useT();
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(term.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [term]);

  const results = useItems(
    { search: debounced, status: 'active', page: 1, pageSize: ITEM_PICKER_PAGE_SIZE },
    // Nothing to list until the operator has typed something.
    { enabled: debounced !== '' },
  );
  // The selected item, looked up by id so an existing node shows its name
  // and SKU rather than a bare id.
  const selected = useItem(value === '' ? undefined : value);

  // Only a 404 means the id is not this tenant's item; any other failure
  // (network, 5xx, 403) is a load error, not a wrong id.
  const selectedMissing =
    selected.isError && isAxiosError(selected.error) && selected.error.response?.status === 404;
  const currentTerm = term.trim();
  const searching = currentTerm.length > 0;
  // A changed input precedes the debounced query by 250 ms. Hide the old
  // term's results immediately, then continue hiding placeholder data while
  // the new query loads. Failed queries must not offer cached rows either.
  const fresh =
    searching && currentTerm === debounced && !results.isPlaceholderData && !results.isError;
  const matches: ItemListEntry[] = fresh ? (results.data?.items ?? []) : [];
  const total = fresh ? (results.data?.total ?? 0) : 0;

  return (
    <div className="grid gap-2">
      {value !== '' && (
        <div
          className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2"
          data-testid="hk-item-selected"
        >
          <div className="min-w-0">
            {selected.isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t('hotkeys.itemPicker.loadingSelected', 'Loading item…')}
              </p>
            ) : selected.data !== undefined ? (
              <p className="text-sm font-medium">
                {selected.data.name}{' '}
                <span className="font-normal text-muted-foreground">· {selected.data.sku}</span>
              </p>
            ) : selectedMissing ? (
              <p className="text-sm text-destructive">
                {t(
                  'hotkeys.itemPicker.selectedMissing',
                  'This item is not in this tenant’s catalogue. Search for it again below.',
                )}
              </p>
            ) : (
              <p className="text-sm text-destructive">
                {t('hotkeys.itemPicker.selectedLoadFailed', 'Could not load this item. Try again.')}
              </p>
            )}
            <p className="truncate text-xs text-muted-foreground">{value}</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            {t('hotkeys.itemPicker.clear', 'Clear')}
          </Button>
        </div>
      )}

      <Input
        id="hk-payload-item"
        type="search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={t('hotkeys.itemPicker.placeholder', 'Search by name, SKU or barcode')}
        autoComplete="off"
      />

      {searching && currentTerm === debounced && results.isError && (
        <p className="text-xs text-destructive">
          {t('hotkeys.itemPicker.loadFailed', 'Could not search the catalogue. Try again.')}
        </p>
      )}
      {searching && currentTerm !== debounced && (
        <p className="text-xs text-muted-foreground">
          {t('hotkeys.itemPicker.searching', 'Searching…')}
        </p>
      )}
      {searching &&
        currentTerm === debounced &&
        !results.isError &&
        (results.isFetching || !fresh) &&
        matches.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {t('hotkeys.itemPicker.searching', 'Searching…')}
          </p>
        )}
      {fresh && !results.isFetching && !results.isError && matches.length === 0 && (
        <p className="text-xs text-muted-foreground">
          {t('hotkeys.itemPicker.noMatches', 'No active item matches “{term}”.').replace(
            '{term}',
            debounced,
          )}
        </p>
      )}

      {matches.length > 0 && (
        <ul
          className="max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border"
          aria-label={t('hotkeys.itemPicker.results', 'Matching items')}
        >
          {matches.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                onClick={() => {
                  onChange({ id: item.id, name: item.name });
                  setTerm('');
                  setDebounced('');
                }}
              >
                <span className="min-w-0 truncate">{item.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {item.sku}
                  {item.primaryBarcode !== null ? ` · ${item.primaryBarcode}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {total > matches.length && (
        <p className="text-xs text-muted-foreground">
          {t('hotkeys.itemPicker.more', 'Showing {shown} of {total}. Type more to narrow the list.')
            .replace('{shown}', String(matches.length))
            .replace('{total}', String(total))}
        </p>
      )}
    </div>
  );
}
